import type { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

import type { FamilySummaryViewModel } from '@/models/family';

interface FamilyCandidatePickerProps {
  readonly candidates: readonly FamilySummaryViewModel[];
  readonly selectedGuid: string | null;
  readonly onSelect: (guid: string) => void;
  readonly loading: boolean;
  readonly isError: boolean;
  readonly hasQuery: boolean;
  readonly showMinLengthHint: boolean;
  readonly minQueryLen: number;
  readonly candidatesAriaLabel: string;
}

/**
 * Single-select picker for family candidates. Each row shows the family name
 * + GUID (the search endpoint doesn't carry member count, so we don't claim
 * one) and includes an external-link affordance that opens the family record
 * in a new tab so the user can verify the match before attaching.
 *
 * The row itself is the radio control; the external-link anchor is a sibling
 * (not nested inside the button) so the markup stays valid and clicks on it
 * don't accidentally toggle selection.
 */
export function FamilyCandidatePicker({
  candidates,
  selectedGuid,
  onSelect,
  loading,
  isError,
  hasQuery,
  showMinLengthHint,
  minQueryLen,
  candidatesAriaLabel,
}: FamilyCandidatePickerProps): ReactElement {
  const { t } = useTranslation();

  if (showMinLengthHint)
    return (
      <Hint
        text={t('patient.sections.family.addToExisting.minLengthHint', {
          count: minQueryLen,
        })}
      />
    );
  if (!hasQuery) return <Hint text={t('patient.sections.family.addToExisting.startTyping')} />;
  if (loading) return <Hint text={t('patient.sections.family.addToExisting.searching')} />;
  if (isError)
    return (
      <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-4 text-center text-xs text-destructive">
        {t('app.unknownError')}
      </p>
    );
  if (candidates.length === 0)
    return <Hint text={t('patient.sections.family.addToExisting.noResults')} />;

  const total = candidates.length;

  return (
    <div
      role="radiogroup"
      aria-label={candidatesAriaLabel}
      className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border"
    >
      {candidates.map((candidate, index) => {
        const isSelected = selectedGuid === candidate.guid;
        const isFocusable = isSelected || (selectedGuid === null && index === 0);

        return (
          <div key={candidate.guid} className="relative">
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isFocusable ? 0 : -1}
              data-candidate-guid={candidate.guid}
              onClick={() => {
                onSelect(candidate.guid);
              }}
              onKeyDown={(e) => {
                handleRowKeyDown(e, index, total, onSelect);
              }}
              className={`flex w-full items-center gap-3 px-3 py-2 pr-20 text-left text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
                isSelected ? 'bg-primary/10' : 'hover:bg-muted'
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-foreground">
                  {candidate.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground tabular-nums">
                  {candidate.guid}
                </span>
              </span>
              {isSelected && (
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                  {t('patient.sections.family.addToExisting.selected')}
                </span>
              )}
            </button>
            <a
              href={`/families/${candidate.guid}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('patient.sections.family.addToExisting.openInNewTabAria', {
                name: candidate.name,
              })}
              title={t('patient.sections.family.addToExisting.openInNewTab')}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>
        );
      })}
    </div>
  );
}

function Hint({ text }: { readonly text: string }): ReactElement {
  return (
    <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
      {text}
    </p>
  );
}

function handleRowKeyDown(
  e: KeyboardEvent<HTMLButtonElement>,
  index: number,
  total: number,
  onSelect: (guid: string) => void,
): void {
  const nextIndex =
    e.key === 'ArrowDown'
      ? (index + 1) % total
      : e.key === 'ArrowUp'
        ? (index - 1 + total) % total
        : e.key === 'Home'
          ? 0
          : e.key === 'End'
            ? total - 1
            : -1;
  if (nextIndex < 0) return;
  e.preventDefault();
  const group = e.currentTarget.closest('[role="radiogroup"]');
  if (group === null) return;
  const items = Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
  const target = items[nextIndex];
  if (target === undefined) return;
  target.focus();
  const guid = target.dataset.candidateGuid;
  if (guid !== undefined) onSelect(guid);
}
